<template>
  <div v-if="!isSubmitted">
    <FormKit
      type="form"
      name="Registrierung"
      id="Registrierung"
      :incomplete-message="false"
      :actions="false"
      @submit="onSubmit"
      #default="{ state: { valid } }"
      form-class="formkit-form--registry dbc-gc-form dbc-formkit-form"
    >
      <FormKit
        v-if="inputParameters"
        v-for="(inputParameter, index) in inputParameters"
        :label="inputParameter.name"
        :placeholder="inputParameter.name"
        :key="inputParameter.id"
        :validation="inputParameter.mandatory ? 'required' : ''"
        :name="inputParameter.name || ''"
        :id="inputParameter.name"
        :type="inputParameter.visible ? 'text' : 'hidden'"
        v-model="inputParameters[index]!.value"
      />
      <FormKit
        type="select"
        :label="`${$t('dbc-label-title')} (${$t('dbc-text-optional')})`"
        name="title"
        id="title"
        value="3197"
        v-model="customer.title"
        :placeholder="$t('dbc-label-title')"
        :options="{
          '3194': $t('dbc-label-title-3194'),
          '3193': $t('dbc-label-title-3193'),
          '3197': $t('dbc-label-title-3197'),
        }"
        inner-class="w-[40%] max-mobile:w-full"
      />
      <FormKit
        v-if="mandatoryFields && mandatoryFields.includes('firstName')"
        type="dbcText"
        name="firstName"
        id="firstName"
        v-model="customer.firstName"
        :label="$t('dbc-label-name')"
        :placeholder="$t('dbc-label-name')"
        validation="required"
        autocomplete="given-name"
      />
      <FormKit
        v-if="mandatoryFields && mandatoryFields.includes('lastName')"
        type="dbcText"
        name="lastName"
        id="lastName"
        v-model="customer.lastName"
        :label="$t('dbc-label-lastname')"
        :placeholder="$t('dbc-label-lastname')"
        validation="required"
        autocomplete="family-name"
      />
      <FormKit
        v-if="mandatoryFields && mandatoryFields.includes('birthdate')"
        type="dbc-input-mask"
        name="birthDate"
        id="birthDate"
        v-model="customer.birthDate"
        :label="$t('dbc-label-birthdate')"
        :placeholder="locale === 'de' ? 'tt.mm.jjjj' : 'dd.mm.yyyy'"
        :mask="'99.99.9999'"
        validation="required|isLegalAge|isBelowMaxAge"
        :validation-rules="{ isLegalAge, isBelowMaxAge }"
        validation-visibility="blur"
        :validation-messages="{
          isLegalAge: birthDateValidationMessage,
          isBelowMaxAge: t('dbc-error-max-age'),
        }"
        autocomplete="bday"
      />

      <FormKit
        v-if="mandatoryFields && mandatoryFields.includes('zipCode')"
        type="dbcText"
        validation="required|length:5"
        name="zipCode"
        id="zipCode"
        v-model="customer.zipCode"
        :label="$t('dbc-label-zipcode')"
        :placeholder="$t('dbc-label-zipcode')"
        autocomplete="postal-code"
      />

      <RegistrationFormPhoneGroup
        v-if="mandatoryFields && mandatoryFields.includes('mobile')"
        @onPhoneValidation="onPhoneValidation"
        v-model:phoneNumber="customer.phoneNumber"
        v-model:country="customer.country"
      />

      <FormKit
        v-if="mandatoryFields && mandatoryFields.includes('mail')"
        type="dbcEmail"
        name="email"
        id="email"
        v-model="customer.email"
        :label="$t('dbc-label-email')"
        :placeholder="$t('dbc-label-email')"
        validation="required|email"
        :validation-messages="{
          email: $t('dbc-error-email-validation'),
        }"
        autocomplete="email"
      />
      <div class="dbc-gc-form__password">
        <FormKit
          v-if="mandatoryFields && mandatoryFields.includes('password')"
          type="password"
          name="password"
          id="password"
          :label="$t('dbc-label-password')"
          :placeholder="$t('dbc-label-password')"
          :validation-rules="{ contains_special_char }"
          validation="required|minlength:8|contains_numeric|contains_special_char|contains_lowercase|contains_uppercase"
          validation-visibility="none"
          prefix-icon="passwordIcon"
          suffix-icon="eyeClosedIcon"
          @suffix-icon-click="(node: FormKitNode ) => togglePasswordIcon(node)"
          @focus="isPasswordInputFocused = true"
          v-model="customer.password"
          autocomplete="new-password"
          help="help"
        >
          <template #help>
            <ul id="help-password" class="mt-xs pl-2xs list-none text-medium">
              <li
                v-for="(validation, key) in passwordValidations"
                :key="key"
                class="flex gap-2xs mb-2xs"
                :aria-hidden="validation.validate(customer.password)"
              >
                <DbcSvg
                  :name="
                    validation.validate(customer.password)
                      ? 'dbc-icon-success'
                      : 'dbc-icon-info'
                  "
                />
                {{ $t(validation['message-key']) }}
              </li>
            </ul>
          </template>
        </FormKit>
      </div>

      <FormKit
        type="submit"
        id="submit"
        :label="$t('dbc-btn-continue')"
        :input-class="'!min-w-[128px] !max-w-[160px]'"
        :disabled="!valid"
      />
    </FormKit>
  </div>
  <div class="c-registration-form__summary" v-if="isSubmitted">
    <RegistrationFormSummary
      :customer="customer"
      :mandatoryFields="mandatoryFields"
      :inputParameters="inputParameters"
      :parsedPhoneNumber="parsedPhoneNumber"
    />
    <RegistrationSuccessTick></RegistrationSuccessTick>
  </div>
</template>

<script setup lang="ts">
import { type FormKitNode } from '@formkit/core'
import type { ParsedPhoneNumber } from 'awesome-phonenumber'
import togglePasswordIcon from '@/formkit/features/togglePasswordIcon'
import passwordValidations from '@/formkit/validations/passwordValidations'
import contains_special_char from '@/formkit/validations/passwordSpecialChar'

const props = defineProps<{
  isSubmitted: boolean
  mandatoryFields: string[] | undefined
  tariffParameters: TariffParameter[] | undefined
  leistungselementid: string
  tariffId: string
}>()

const { $isLegalAge, $isBelowMaxAge, $formatDateToYYYYMMDD } = useNuxtApp()

const customer = ref<RegistrationPersonalData>({
  title: '',
  firstName: '',
  lastName: '',
  birthDate: '',
  email: '',
  password: '',
  zipCode: '',
  country: 'DE',
  phoneNumber: '',
})

const inputParameters = ref<TariffParameter[]>()

setInputParameters()

watch(
  () => props.tariffParameters,
  () => {
    setInputParameters()
  }
)

function setInputParameters() {
  inputParameters.value = props.tariffParameters?.map((inputParameter) => {
    return {
      name: inputParameter.name,
      mandatory: inputParameter.mandatory,
      visible: inputParameter.visible,
      value:
        inputParameter.reference === 'new_comfort_id'
          ? props.leistungselementid
          : '',
      id: inputParameter.id,
    }
  })
}

const { t, locale } = useI18n()

const emit = defineEmits<{
  (e: 'onSubmit', formData: RegistrationPersonalData): void
}>()

const parsedPhoneNumber = ref()

function onPhoneValidation(pn: ParsedPhoneNumber) {
  parsedPhoneNumber.value = pn
  customer.value.phoneNumber = pn.number?.input
}

function onSubmit(formData: RegistrationPersonalData) {
  if (props.mandatoryFields && props.mandatoryFields.includes('mobile')) {
    formData.phoneNumber = parsedPhoneNumber.value.number.e164
  }

  formData.inputParameters = inputParameters.value

  if (props.mandatoryFields && props.mandatoryFields.includes('birthdate')) {
    formData.birthDate = $formatDateToYYYYMMDD(formData.birthDate)
  }

  emit('onSubmit', formData)
}

const legalAge = ref(18)
const birthDateValidationMessage = ref('')
setLegalAge()
watch(
  () => props.tariffId,
  () => {
    setLegalAge()
  }
)

function setLegalAge() {
  legalAge.value =
    props.tariffId === 'DB_Mitarbeitende' ||
    props.tariffId === 'DBC_Mitarbeitende'
      ? 14
      : 18

  birthDateValidationMessage.value = t('dbc-error-legal-age').replace(
    '[placeholder]',
    legalAge.value.toString()
  )
}

function isLegalAge(node: FormKitNode) {
  return $isLegalAge(node.value as string, legalAge.value)
}

function isBelowMaxAge(node: FormKitNode) {
  return $isBelowMaxAge(node.value as string)
}

const isPasswordInputFocused = ref(false)
</script>
